import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Gift as GiftIcon, Upload, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminGifts() {
  const [gifts, setGifts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGift, setEditingGift] = useState(null);
  const [fetchUrl, setFetchUrl] = useState('');
  const [fetching, setFetching] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    target_age_range: '',
    budget_tier: '',
    gift_type: '',
    description: '',
    why_thoughtful: '',
    best_timing: '',
    occasion_tags: '',
    developmental_value: '',
    indoor_outdoor: '',
    individual_group: '',
    image_url: '',
    buy_link: '',
  });

  useEffect(() => {
    fetchGifts();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
      // Only set default category if we're not editing and no category is set
      if (response.data.length > 0 && !editingGift && !formData.category) {
        setFormData(prev => ({ ...prev, category: response.data[0].slug }));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchGifts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/gifts`);
      setGifts(response.data);
    } catch (error) {
      console.error('Error fetching gifts:', error);
      toast.error('Failed to load gifts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    const giftData = {
      ...formData,
      occasion_tags: formData.occasion_tags.split(',').map(t => t.trim()).filter(Boolean),
    };

    try {
      if (editingGift) {
        await axios.put(`${API}/gifts/${editingGift.id}`, giftData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Gift updated successfully');
      } else {
        await axios.post(`${API}/gifts`, giftData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Gift added successfully');
      }
      fetchGifts();
      handleCloseDialog();
    } catch (error) {
      toast.error('Failed to save gift');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this gift?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/gifts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Gift deleted successfully');
      fetchGifts();
    } catch (error) {
      toast.error('Failed to delete gift');
    }
  };

  const handleEdit = async (gift) => {
    setEditingGift(gift);
    
    // Ensure categories are loaded before setting form data
    if (categories.length === 0) {
      await fetchCategories();
    }
    
    setFormData({
      ...gift,
      occasion_tags: gift.occasion_tags.join(', '),
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingGift(null);
    setFetchUrl('');
    const defaultCategory = categories.length > 0 ? categories[0].slug : '';
    setFormData({
      title: '',
      category: defaultCategory,
      target_age_range: '',
      budget_tier: '',
      gift_type: '',
      description: '',
      why_thoughtful: '',
      best_timing: '',
      occasion_tags: '',
      developmental_value: '',
      indoor_outdoor: '',
      individual_group: '',
      image_url: '',
      buy_link: '',
    });
  };

  const getCategoryName = (slug) => {
    const category = categories.find(cat => cat.slug === slug);
    return category ? category.name : slug;
  };

  const MOM_SLUGS = ['new-moms', 'expecting-moms', 'expecting-new-moms', 'busy-moms'];
  const KID_SLUGS = ['kids', 'toddlers', 'non-toy-gifts-for-kids'];
  const isMom = MOM_SLUGS.includes(formData.category);
  const isKid = KID_SLUGS.includes(formData.category);

  const handleFetchMetadata = async () => {
    if (!fetchUrl.trim()) {
      toast.error('Paste a product link first');
      return;
    }
    const token = localStorage.getItem('admin_token');
    setFetching(true);
    try {
      const { data } = await axios.post(`${API}/admin/fetch-metadata`, { url: fetchUrl.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFormData((prev) => ({
        ...prev,
        buy_link: data.buy_link || prev.buy_link,
        image_url: data.image_url || prev.image_url,
        title: prev.title || data.title || '',
        description: prev.description || data.description || '',
      }));
      toast.success('Details fetched! Review and adjust before saving.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not fetch details from that link');
    } finally {
      setFetching(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem('admin_token');
    const formDataImg = new FormData();
    formDataImg.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formDataImg, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setFormData({ ...formData, image_url: response.data.image_url });
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Image upload failed');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-heading text-4xl font-light text-primary" data-testid="admin-gifts-title">
          Manage Gifts
        </h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingGift(null)} data-testid="admin-gifts-add-button">
              <Plus className="mr-2 h-4 w-4" />
              Add Gift
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingGift ? 'Edit Gift' : 'Add New Gift'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 space-y-2" data-testid="gift-autofill-box">
                <label className="font-body text-sm font-semibold flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-accent" />
                  Auto-fill from a product link
                </label>
                <p className="text-xs text-muted-foreground">Paste an Amazon or store link — we'll pull the title, cover image and description automatically.</p>
                <div className="flex gap-2">
                  <Input
                    value={fetchUrl}
                    onChange={(e) => setFetchUrl(e.target.value)}
                    placeholder="https://www.amazon.in/..."
                    data-testid="gift-autofill-input"
                  />
                  <Button type="button" onClick={handleFetchMetadata} disabled={fetching} data-testid="gift-autofill-button">
                    {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fetch'}
                  </Button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="font-body text-sm font-medium mb-1 block">Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    data-testid="gift-form-title"
                  />
                </div>
                <div>
                  <label className="font-body text-sm font-medium mb-1 block">Category *</label>
                  {categories.length > 0 ? (
                    <Select 
                      key={`category-${editingGift?.id || 'new'}`}
                      value={formData.category} 
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="gift-form-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input disabled placeholder="Loading categories..." />
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="font-body text-sm font-medium mb-1 block">Budget Tier</label>
                  <Select value={formData.budget_tier} onValueChange={(value) => setFormData({ ...formData, budget_tier: value })}>
                    <SelectTrigger data-testid="gift-form-budget">
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Under ₹1,000">Under ₹1,000</SelectItem>
                      <SelectItem value="₹1,000-2,000">₹1,000-2,000</SelectItem>
                      <SelectItem value="₹2,000-4,000">₹2,000-4,000</SelectItem>
                      <SelectItem value="₹3,000-6,000">₹3,000-6,000</SelectItem>
                      <SelectItem value="₹6,000-10,000">₹6,000-10,000</SelectItem>
                      <SelectItem value="₹8,000+">₹8,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isKid && (
                  <div>
                    <label className="font-body text-sm font-medium mb-1 block">Age Range</label>
                    <Select value={formData.target_age_range} onValueChange={(value) => setFormData({ ...formData, target_age_range: value })}>
                      <SelectTrigger data-testid="gift-form-age">
                        <SelectValue placeholder="Select age" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-2 years">0-2 years</SelectItem>
                        <SelectItem value="2-4 years">2-4 years</SelectItem>
                        <SelectItem value="3-6 years">3-6 years</SelectItem>
                        <SelectItem value="6+ years">6+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {isMom && (
                  <div>
                    <label className="font-body text-sm font-medium mb-1 block">Gift Type</label>
                    <Select value={formData.gift_type} onValueChange={(value) => setFormData({ ...formData, gift_type: value })}>
                      <SelectTrigger data-testid="gift-form-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Practical">Practical</SelectItem>
                        <SelectItem value="Emotional">Emotional</SelectItem>
                        <SelectItem value="Luxury">Luxury</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div>
                <label className="font-body text-sm font-medium mb-1 block">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="gift-form-description"
                />
              </div>

              <div>
                <label className="font-body text-sm font-medium mb-1 block">
                  {isMom ? 'Why It\'s Thoughtful' : 'Developmental Value'}
                </label>
                <Textarea
                  value={isMom ? formData.why_thoughtful : formData.developmental_value}
                  onChange={(e) => {
                    if (isMom) {
                      setFormData({ ...formData, why_thoughtful: e.target.value });
                    } else {
                      setFormData({ ...formData, developmental_value: e.target.value });
                    }
                  }}
                  rows={3}
                  data-testid="gift-form-value"
                />
              </div>

              {isMom && (
                <div>
                  <label className="font-body text-sm font-medium mb-1 block">Best Timing to Give</label>
                  <Input
                    value={formData.best_timing}
                    onChange={(e) => setFormData({ ...formData, best_timing: e.target.value })}
                    placeholder="e.g., First month postpartum"
                    data-testid="gift-form-timing"
                  />
                </div>
              )}

              <div>
                <label className="font-body text-sm font-medium mb-1 block">Occasion Tags (comma-separated)</label>
                <Input
                  value={formData.occasion_tags}
                  onChange={(e) => setFormData({ ...formData, occasion_tags: e.target.value })}
                  placeholder="Birthday, Baby Shower, Any Time"
                  data-testid="gift-form-occasions"
                />
              </div>

              <div>
                <label className="font-body text-sm font-medium mb-1 block">Image</label>
                <div className="flex gap-2">
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="Image URL or upload below"
                    data-testid="gift-form-image-url"
                  />
                  <label className="cursor-pointer">
                    <Button type="button" variant="outline" asChild>
                      <span>
                        <Upload className="h-4 w-4" />
                      </span>
                    </Button>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                </div>
              </div>

              <div>
                <label className="font-body text-sm font-medium mb-1 block">Buy Link</label>
                <Input
                  value={formData.buy_link}
                  onChange={(e) => setFormData({ ...formData, buy_link: e.target.value })}
                  placeholder="https://..."
                  data-testid="gift-form-buy-link"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseDialog} data-testid="gift-form-cancel">
                  Cancel
                </Button>
                <Button type="submit" data-testid="gift-form-submit">
                  {editingGift ? 'Update' : 'Add'} Gift
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading gifts...</p>
        </div>
      ) : gifts.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <GiftIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4" data-testid="admin-gifts-empty">No gifts yet. Add your first gift!</p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-gifts-table">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-4 font-body text-sm font-semibold">Title</th>
                  <th className="text-left p-4 font-body text-sm font-semibold">Category</th>
                  <th className="text-left p-4 font-body text-sm font-semibold">Budget</th>
                  <th className="text-left p-4 font-body text-sm font-semibold">Age Range</th>
                  <th className="text-right p-4 font-body text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {gifts.map((gift) => (
                  <tr key={gift.id} className="border-t border-border hover:bg-muted/50">
                    <td className="p-4 font-body">{gift.title}</td>
                    <td className="p-4 font-body text-sm">
                      <span className="inline-block px-2 py-1 rounded text-xs bg-accent/10 text-accent">
                        {getCategoryName(gift.category)}
                      </span>
                    </td>
                    <td className="p-4 font-body text-sm">{gift.budget_tier || '—'}</td>
                    <td className="p-4 font-body text-sm">{gift.target_age_range || '—'}</td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(gift)}
                          data-testid={`gift-edit-${gift.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(gift.id)}
                          data-testid={`gift-delete-${gift.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
